import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './BBMappingPage.css'; // Reuse same styles

/**
 * VendorTypeMappingPage - Admin tool for mapping master_items to vendor types (trades).
 * Each master_item gets a primary vendor_type_id (e.g., נגרות, חשמל, אינסטלציה).
 * AI suggestions stored in vendor_type_mapping_suggestions table (persists across reloads).
 * Admin approves → writes to master_items.vendor_type_id. Can also manually override.
 * Access via /admin/vendor-type-mapping (no sidebar link needed).
 */
const VendorTypeMappingPage = () => {
  // Data state
  const [masterItems, setMasterItems] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [suggestions, setSuggestions] = useState({}); // keyed by master_item_id
  const [itemTypes, setItemTypes] = useState({});
  const [loading, setLoading] = useState(true);

  // UI state
  const [filter, setFilter] = useState('work'); // work, all, unmapped, pending, mapped, low-confidence
  const [searchTerm, setSearchTerm] = useState('');
  const [filterByItemType, setFilterByItemType] = useState('');
  const [apiKey] = useState(process.env.REACT_APP_CLAUDE_API_KEY || '');
  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, vendorTypesRes, suggestionsRes, typesRes] = await Promise.all([
        supabase
          .from('master_items')
          .select(
            'id, item_name, item_type_id, vendor_type_id, is_bundle, requires_gc_prep, include_in_boq',
          )
          .eq('is_bundle', false)
          .order('item_name'),
        supabase
          .from('reference_vendor_types')
          .select('id, name, category, display_order')
          .order('category, display_order'),
        supabase.from('vendor_type_mapping_suggestions').select('*'),
        supabase.from('reference_item_types').select('id, name'),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (vendorTypesRes.error) throw vendorTypesRes.error;
      if (suggestionsRes.error) throw suggestionsRes.error;
      if (typesRes.error) throw typesRes.error;

      setMasterItems(itemsRes.data || []);
      setVendorTypes(vendorTypesRes.data || []);

      // Index suggestions by master_item_id
      const sugMap = {};
      (suggestionsRes.data || []).forEach(s => {
        sugMap[s.master_item_id] = s;
      });
      setSuggestions(sugMap);

      // Index item types by id
      const typeMap = {};
      (typesRes.data || []).forEach(t => {
        typeMap[t.id] = t.name;
      });
      setItemTypes(typeMap);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Computed values ─────────────────────────────────────────────

  // Helper: check if an item is "mapped" (approved or manual in suggestions, or has vendor_type_id)
  const isMapped = useCallback(
    itemId => {
      const s = suggestions[itemId];
      const item = masterItems.find(i => i.id === itemId);
      return item?.vendor_type_id || (s && (s.status === 'approved' || s.status === 'manual'));
    },
    [suggestions, masterItems],
  );

  // Helper: get the current mapped vendor type ID (from suggestions or master_items)
  const getMappedVendorTypeId = useCallback(
    itemId => {
      const s = suggestions[itemId];
      if (s && (s.status === 'approved' || s.status === 'manual')) {
        return s.suggested_vendor_type_id;
      }
      const item = masterItems.find(i => i.id === itemId);
      return item?.vendor_type_id || '';
    },
    [suggestions, masterItems],
  );

  const stats = useMemo(() => {
    const total = masterItems.length;
    const mapped = masterItems.filter(i => isMapped(i.id)).length;
    const pending = Object.values(suggestions).filter(s => s.status === 'pending').length;
    const approved = Object.values(suggestions).filter(
      s => s.status === 'approved' || s.status === 'manual',
    ).length;
    // Working set: exclude include_in_boq items, show only unmapped
    const workItems = masterItems.filter(i => !i.include_in_boq && !isMapped(i.id)).length;
    return { total, mapped, unmapped: total - mapped, pending, approved, workItems };
  }, [masterItems, suggestions, isMapped]);

  // Group vendor types by category for the dropdown
  const vendorTypesByCategory = useMemo(() => {
    const groups = {};
    vendorTypes.forEach(vt => {
      const cat = vt.category || 'אחר';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(vt);
    });
    return groups;
  }, [vendorTypes]);

  // Vendor type lookup map
  const vendorTypeMap = useMemo(() => {
    const map = {};
    vendorTypes.forEach(vt => {
      map[vt.id] = vt;
    });
    return map;
  }, [vendorTypes]);

  // Filter and search items
  const filteredItems = useMemo(() => {
    let items = masterItems;

    // Apply filter
    switch (filter) {
      case 'work':
        // Exclude BOQ items, show only unmapped
        items = items.filter(i => !i.include_in_boq && !isMapped(i.id));
        break;
      case 'unmapped':
        items = items.filter(i => !isMapped(i.id));
        break;
      case 'pending':
        items = items.filter(i => suggestions[i.id]?.status === 'pending');
        break;
      case 'mapped':
        items = items.filter(i => isMapped(i.id));
        break;
      case 'low-confidence':
        items = items.filter(i => {
          const s = suggestions[i.id];
          return s && s.confidence < 0.8;
        });
        break;
      default:
        break;
    }

    // Apply item type filter
    if (filterByItemType) {
      items = items.filter(i => i.item_type_id === filterByItemType);
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(
        i =>
          i.item_name.toLowerCase().includes(term) ||
          (itemTypes[i.item_type_id] || '').toLowerCase().includes(term),
      );
    }

    return items;
  }, [masterItems, filter, filterByItemType, searchTerm, suggestions, itemTypes, isMapped]);

  // ─── Actions ─────────────────────────────────────────────────────

  // Approve an AI suggestion → write to suggestions table only (not master_items yet)
  const handleApprove = async itemId => {
    const suggestion = suggestions[itemId];
    if (!suggestion?.suggested_vendor_type_id) return;

    try {
      const { error } = await supabase
        .from('vendor_type_mapping_suggestions')
        .update({ status: 'approved' })
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], status: 'approved' },
      }));
    } catch (err) {
      console.error('Error approving suggestion:', err);
      alert('שגיאה באישור: ' + err.message);
    }
  };

  // Reject an AI suggestion
  const handleReject = async itemId => {
    const suggestion = suggestions[itemId];
    if (!suggestion) return;

    try {
      const { error } = await supabase
        .from('vendor_type_mapping_suggestions')
        .update({ status: 'rejected' })
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], status: 'rejected' },
      }));
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
      alert('שגיאה בדחייה: ' + err.message);
    }
  };

  // Manual override → write to suggestions table as 'manual'
  const handleManualMap = async (itemId, vendorTypeId) => {
    if (!vendorTypeId) return;

    try {
      const { error } = await supabase.from('vendor_type_mapping_suggestions').upsert(
        {
          master_item_id: itemId,
          suggested_vendor_type_id: vendorTypeId,
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: 'Manual mapping by admin',
        },
        { onConflict: 'master_item_id' },
      );
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          master_item_id: itemId,
          suggested_vendor_type_id: vendorTypeId,
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: 'Manual mapping by admin',
        },
      }));
    } catch (err) {
      console.error('Error setting manual mapping:', err);
      alert('שגיאה בשמירה: ' + err.message);
    }
  };

  // Toggle requires_gc_prep on master_items
  const handleToggleGcPrep = async (itemId, value) => {
    try {
      const { error } = await supabase
        .from('master_items')
        .update({ requires_gc_prep: value })
        .eq('id', itemId);
      if (error) throw error;

      setMasterItems(prev =>
        prev.map(i => (i.id === itemId ? { ...i, requires_gc_prep: value } : i)),
      );
    } catch (err) {
      console.error('Error toggling requires_gc_prep:', err);
      alert('שגיאה בעדכון: ' + err.message);
    }
  };

  // Clear a mapping (delete suggestion)
  const handleClearMapping = async itemId => {
    try {
      const { error } = await supabase
        .from('vendor_type_mapping_suggestions')
        .delete()
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    } catch (err) {
      console.error('Error clearing mapping:', err);
      alert('שגיאה במחיקה: ' + err.message);
    }
  };

  // Batch approve all high-confidence suggestions (≥0.8)
  const handleBatchApprove = async () => {
    const highConfidence = masterItems.filter(i => {
      const s = suggestions[i.id];
      return s && s.status === 'pending' && s.confidence >= 0.8 && !isMapped(i.id);
    });

    if (highConfidence.length === 0) {
      alert('אין הצעות ברמת ביטחון גבוהה לאישור');
      return;
    }

    if (!window.confirm(`לאשר ${highConfidence.length} הצעות ברמת ביטחון גבוהה (≥0.8)?`)) return;

    for (const item of highConfidence) {
      await handleApprove(item.id);
    }
  };

  // Batch apply: write all approved/manual suggestions to master_items.vendor_type_id
  const handleBatchApply = async () => {
    const toApply = masterItems.filter(i => {
      const s = suggestions[i.id];
      return (
        s &&
        (s.status === 'approved' || s.status === 'manual') &&
        i.vendor_type_id !== s.suggested_vendor_type_id &&
        !i.include_in_boq // Skip BOQ items — they get their trade from the BOQ system
      );
    });

    if (toApply.length === 0) {
      alert('אין מיפויים מאושרים להחלה');
      return;
    }

    if (!window.confirm(`להחיל ${toApply.length} מיפויים מאושרים על master_items?`)) return;

    let applied = 0;
    for (const item of toApply) {
      const s = suggestions[item.id];
      try {
        const { error } = await supabase
          .from('master_items')
          .update({ vendor_type_id: s.suggested_vendor_type_id })
          .eq('id', item.id);
        if (error) throw error;

        setMasterItems(prev =>
          prev.map(i =>
            i.id === item.id ? { ...i, vendor_type_id: s.suggested_vendor_type_id } : i,
          ),
        );
        applied++;
      } catch (err) {
        console.error(`Error applying mapping for ${item.item_name}:`, err);
      }
    }

    alert(`הוחלו ${applied} מיפויים בהצלחה`);
  };

  // Bulk map all items of a specific item_type to a vendor_type (via suggestions)
  const handleBulkMapByItemType = async (typeId, vendorTypeId) => {
    if (!typeId || !vendorTypeId) return;

    const items = masterItems.filter(i => i.item_type_id === typeId && !isMapped(i.id));
    if (items.length === 0) {
      alert('אין פריטים לא ממופים לסוג זה');
      return;
    }

    const typeName = itemTypes[typeId] || typeId;
    const vtName = vendorTypeMap[vendorTypeId]?.name || vendorTypeId;
    if (!window.confirm(`למפות ${items.length} פריטים מסוג "${typeName}" ל"${vtName}"?`)) return;

    for (const item of items) {
      await handleManualMap(item.id, vendorTypeId);
    }
  };

  // ─── Claude AI ───────────────────────────────────────────────────

  const runAiMapping = async () => {
    if (!apiKey) {
      alert('נא להזין Claude API Key');
      return;
    }

    // Only map items without suggestions AND without existing vendor_type_id
    const unmappedItems = masterItems.filter(i => !isMapped(i.id) && !suggestions[i.id]);
    if (unmappedItems.length === 0) {
      alert('כל הפריטים כבר ממופים או יש להם הצעות');
      return;
    }

    setAiRunning(true);
    setAiProgress({ current: 0, total: unmappedItems.length });

    // Build vendor type list for the prompt
    const vtList = vendorTypes.map(vt => `${vt.name} (${vt.category}) [id: ${vt.id}]`).join('\n');

    // Process in batches of 10 for efficiency
    const batchSize = 10;
    for (let batchStart = 0; batchStart < unmappedItems.length; batchStart += batchSize) {
      const batch = unmappedItems.slice(batchStart, batchStart + batchSize);
      setAiProgress({ current: batchStart, total: unmappedItems.length });

      // Rate limit between batches
      if (batchStart > 0) await new Promise(r => setTimeout(r, 2000));

      try {
        const itemsList = batch
          .map((item, idx) => {
            const typeName = itemTypes[item.item_type_id] || 'לא ידוע';
            return `${idx + 1}. "${item.item_name}" (סוג: ${typeName})`;
          })
          .join('\n');

        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 1500,
            messages: [
              {
                role: 'user',
                content: `You are mapping Israeli construction/renovation items to vendor trade types.
Each item should be assigned to the vendor type that would typically SUPPLY or INSTALL it.

Items to map:
${itemsList}

Available vendor types:
${vtList}

For each item, pick the BEST matching vendor type ID.
Also provide a confidence score (0.0 to 1.0) — use 0.9+ for obvious matches, 0.5-0.8 for reasonable guesses.
If no good match, use the "אחר" type with low confidence.

Respond ONLY as a JSON array, one object per item, in order:
[{"item_index": 1, "vendor_type_id": "uuid-here", "confidence": 0.9, "reasoning": "brief Hebrew explanation"}]`,
              },
            ],
          }),
        });

        if (!response.ok) {
          const errText = await response.text();
          console.error(`[AI MAP] API error:`, errText);
          continue;
        }

        const data = await response.json();
        const text = data.content?.[0]?.text || '';

        // Parse the JSON array response
        const jsonMatch = text.match(/\[[\s\S]*\]/);
        if (!jsonMatch) {
          console.error(`[AI MAP] Could not parse response:`, text);
          continue;
        }

        const parsed = JSON.parse(jsonMatch[0]);

        // Save each suggestion to the DB
        for (const mapping of parsed) {
          const itemIdx = mapping.item_index - 1;
          if (itemIdx < 0 || itemIdx >= batch.length) continue;

          const item = batch[itemIdx];
          const vtId = mapping.vendor_type_id;

          // Verify the vendor type ID exists
          if (!vendorTypeMap[vtId]) {
            console.error(`[AI MAP] Unknown vendor type ID "${vtId}" for "${item.item_name}"`);
            continue;
          }

          console.log(
            `[AI MAP] ✓ "${item.item_name}" → ${vendorTypeMap[vtId].name} (${mapping.confidence}) — ${mapping.reasoning}`,
          );

          // Save suggestion to DB (persists across reloads)
          const { error: insertError } = await supabase
            .from('vendor_type_mapping_suggestions')
            .upsert(
              {
                master_item_id: item.id,
                suggested_vendor_type_id: vtId,
                confidence: mapping.confidence || 0.5,
                ai_reasoning: mapping.reasoning,
                status: 'pending',
              },
              { onConflict: 'master_item_id' },
            );

          if (insertError) {
            console.error(`Error saving suggestion for ${item.item_name}:`, insertError);
            continue;
          }

          // Update local state
          setSuggestions(prev => ({
            ...prev,
            [item.id]: {
              master_item_id: item.id,
              suggested_vendor_type_id: vtId,
              confidence: mapping.confidence || 0.5,
              ai_reasoning: mapping.reasoning,
              status: 'pending',
            },
          }));
        }
      } catch (err) {
        console.error(`Error processing batch:`, err);
      }
    }

    setAiProgress({ current: unmappedItems.length, total: unmappedItems.length });
    setAiRunning(false);
  };

  // ─── Render helpers ────────────────────────────────────────────

  const getConfidenceClass = confidence => {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.5) return 'confidence-medium';
    return 'confidence-low';
  };

  // ─── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bb-mapping-page">
        <div className="bb-mapping-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="bb-mapping-page">
      {/* Header */}
      <div className="bb-mapping-header">
        <h1>מיפוי פריטים לסוגי ספקים</h1>
        <div className="bb-mapping-header-actions">
          <input
            type="text"
            className="bb-mapping-search"
            placeholder="חיפוש פריט..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Progress */}
      <div className="bb-mapping-progress">
        <div className="bb-mapping-progress-bar">
          <div
            className="bb-mapping-progress-fill"
            style={{ width: `${stats.total ? (stats.mapped / stats.total) * 100 : 0}%` }}
          />
        </div>
        <div className="bb-mapping-progress-text">
          {stats.mapped}/{stats.total} ממופים
          {stats.pending > 0 && ` | ${stats.pending} ממתינים לאישור`}
        </div>
      </div>

      {/* AI actions */}
      <div className="api-key-section">
        <span className="api-key-label">
          Claude API: {apiKey ? '✓ מחובר' : '✗ חסר REACT_APP_CLAUDE_API_KEY ב-.env'}
        </span>
        <button
          className="bb-action-btn primary"
          onClick={runAiMapping}
          disabled={aiRunning || !apiKey}
        >
          {aiRunning
            ? `ממפה... (${aiProgress.current}/${aiProgress.total})`
            : `הרץ מיפוי AI (${stats.unmapped - stats.pending} חסרים)`}
        </button>
        <button className="bb-action-btn approve" onClick={handleBatchApprove} disabled={aiRunning}>
          אשר הכל (ביטחון ≥0.8)
        </button>
        <button className="bb-action-btn primary" onClick={handleBatchApply} disabled={aiRunning}>
          החל מאושרים על master_items
        </button>
      </div>

      {/* Bulk map by item type */}
      <div className="api-key-section">
        <span className="api-key-label">מפה הכל לפי סוג פריט:</span>
        <select
          className="chapter-select"
          style={{ minWidth: 160 }}
          value={filterByItemType}
          onChange={e => setFilterByItemType(e.target.value)}
        >
          <option value="">כל הסוגים</option>
          {Object.entries(itemTypes)
            .sort((a, b) => a[1].localeCompare(b[1], 'he'))
            .map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
        </select>
        {filterByItemType && (
          <select
            className="chapter-select"
            style={{ minWidth: 220 }}
            value=""
            onChange={e => {
              if (e.target.value) handleBulkMapByItemType(filterByItemType, e.target.value);
            }}
          >
            <option value="">מפה כולם ל...</option>
            {Object.entries(vendorTypesByCategory).map(([category, types]) => (
              <optgroup key={category} label={category}>
                {types.map(vt => (
                  <option key={vt.id} value={vt.id}>
                    {vt.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
      </div>

      {/* Filters */}
      <div className="bb-mapping-filters">
        {[
          { key: 'work', label: `לעבודה (${stats.workItems})` },
          { key: 'all', label: `הכל (${stats.total})` },
          { key: 'unmapped', label: `לא ממופים (${stats.unmapped})` },
          { key: 'pending', label: `ממתינים (${stats.pending})` },
          { key: 'mapped', label: `ממופים (${stats.mapped})` },
          { key: 'low-confidence', label: 'ביטחון נמוך' },
        ].map(f => (
          <button
            key={f.key}
            className={`bb-mapping-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bb-mapping-table-container">
        <table className="bb-mapping-table">
          <thead>
            <tr>
              <th>#</th>
              <th>שם פריט</th>
              <th>סוג פריט</th>
              <th>הצעת AI</th>
              <th>ביטחון</th>
              <th>מיפוי נוכחי</th>
              <th>נימוק</th>
              <th>הכנה לקבלן</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={8} className="bb-mapping-empty">
                  אין פריטים להצגה
                </td>
              </tr>
            ) : (
              filteredItems.map((item, idx) => {
                const suggestion = suggestions[item.id];

                return (
                  <tr key={item.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <strong>{item.item_name}</strong>
                    </td>
                    <td>
                      <span className="item-type-badge">{itemTypes[item.item_type_id] || '-'}</span>
                    </td>
                    <td>
                      <div className="bb-mapping-actions-cell">
                        {suggestion?.suggested_vendor_type_id
                          ? vendorTypeMap[suggestion.suggested_vendor_type_id]?.name || '-'
                          : '-'}
                        {suggestion?.status === 'pending' &&
                          suggestion?.suggested_vendor_type_id && (
                            <>
                              <button
                                className="bb-action-btn approve"
                                onClick={() => handleApprove(item.id)}
                              >
                                אשר
                              </button>
                              <button
                                className="bb-action-btn reject"
                                onClick={() => handleReject(item.id)}
                              >
                                דחה
                              </button>
                            </>
                          )}
                        {suggestion?.status === 'rejected' && (
                          <span className="status-badge status-rejected">נדחה</span>
                        )}
                        {suggestion?.status === 'approved' && (
                          <span className="status-badge status-approved">אושר</span>
                        )}
                        {suggestion?.status === 'manual' && (
                          <span className="status-badge status-manual">ידני</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {suggestion?.confidence != null ? (
                        <span
                          className={`confidence-badge ${getConfidenceClass(suggestion.confidence)}`}
                        >
                          {(suggestion.confidence * 100).toFixed(0)}%
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <select
                          className={`chapter-select${getMappedVendorTypeId(item.id) ? ' mapped' : ''}`}
                          value={getMappedVendorTypeId(item.id)}
                          onChange={e => handleManualMap(item.id, e.target.value)}
                        >
                          <option value="">-- בחר סוג ספק --</option>
                          {Object.entries(vendorTypesByCategory).map(([category, types]) => (
                            <optgroup key={category} label={category}>
                              {types.map(vt => (
                                <option key={vt.id} value={vt.id}>
                                  {vt.name}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        {getMappedVendorTypeId(item.id) && (
                          <button
                            className="bb-action-btn reject"
                            style={{ flexShrink: 0 }}
                            onClick={() => handleClearMapping(item.id)}
                            title="נקה מיפוי"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      {suggestion?.ai_reasoning ? (
                        <span className="ai-reasoning" title={suggestion.ai_reasoning}>
                          {suggestion.ai_reasoning}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={item.requires_gc_prep || false}
                        onChange={() => handleToggleGcPrep(item.id, !item.requires_gc_prep)}
                        title="הכנה לקבלן ראשי"
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorTypeMappingPage;
